import { motion } from 'framer-motion';
import { Truck, Box, Navigation, Award, Zap, Clock, MapPin, ArrowRight, Phone, Mail } from 'lucide-react';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/ui/SEO';
import { services, whyChooseUs, companyInfo } from '../data';

const iconMap = {
  Truck: Truck,
  Box: Box,
  Navigation: Navigation,
  Award: Award,
  Zap: Zap,
  Clock: Clock,
  MapPin: MapPin
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

export default function Home() {
  return (
    <>
      <SEO 
        title="Forside" 
        description="NR. GALTEN VOGNMANDSFORRETNING ApS tilbyder transport med salg og levering af materialer, jordflytning og containerudlejning i lokalområdet. 20 års erfaring." 
      />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[80vh] flex items-center pt-32 md:pt-24 pb-12 md:pb-20 overflow-hidden bg-slate-50">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-900 hidden lg:block skew-x-[-12deg] translate-x-20 z-0"></div>
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50 z-0"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-12 md:pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="max-w-2xl"
            >
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center space-x-2 bg-blue-50 text-blue-900 px-4 py-2 rounded-full text-sm font-bold mb-6 border border-blue-100"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                </span>
                <span>Din pålidelige vognmandspartner</span>
              </motion.div>
              
              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[1.1] text-slate-900">
                Transport, <span className="text-blue-900">jordflytning</span> & containerleje
              </h1>
              <p className="text-xl mb-10 text-slate-600 leading-relaxed max-w-xl">
                Vi forener 20 års erfaring med pålidelige løsninger. Vi tilbyder transport med salg og levering, jordflytning og containerudlejning i lokalområdet.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-5">
                <Button to="/kontakt" variant="primary" className="group">
                  Få et tilbud
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button to="/transport" variant="secondary">
                  Vores ydelser
                </Button>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative hidden lg:block"
            >
              <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white">
                <img 
                  src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/11d8ac04-b4a2-4792-97df-3ed8383a3251.jpg" 
                  alt="Vores lastbil - NR. GALTEN VOGNMANDSFORRETNING ApS" 
                  className="w-full h-[600px] object-cover"
                />
              </div>
              {/* Floating Card */}
              <motion.div 
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-4 -left-10 bg-white p-8 rounded-3xl shadow-2xl z-20 max-w-xs border border-slate-100"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Erfaring</p>
                    <p className="text-xl font-black text-slate-900">20 År</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Vi har leveret kvalitetstransport i lokalområdet i 20 år med fokus på gode aftaler.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview - Asymmetrical Grid */}
      <Section id="services" className="overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-sm uppercase tracking-[0.3em] font-black text-blue-900 mb-4">Vores Ekspertise</h2>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
              Skræddersyede løsninger til <span className="text-blue-900">ethvert behov</span>
            </h3>
          </div>
          <p className="text-slate-600 max-w-md text-lg">
            Vi kombinerer 20 års erfaring med personlig service for at sikre dig den bedste løsning.
          </p>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`group relative bg-white rounded-[2.5rem] p-6 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_40px_80px_rgba(30,58,138,0.15)] transition-all duration-500 border border-slate-100 ${
                  index === 1 ? 'md:-translate-y-12 lg:-translate-y-12' : ''
                } ${index === 2 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              >
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                  <Icon className="w-32 h-32" />
                </div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-8 text-blue-900 group-hover:bg-blue-900 group-hover:text-white transition-colors duration-500 shadow-inner">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h4 className="text-2xl font-black text-slate-900 mb-4">{service.title}</h4>
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <Button to={service.href} variant="ghost" className="p-0 text-blue-900 font-black hover:bg-transparent group-hover:translate-x-2 transition-transform">
                    Udforsk service <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </Section>

      {/* Why Choose Us - Split Layout with Visual Interest */}
      <Section light className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-50/50 -skew-x-12 translate-x-20"></div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10">
          <div className="relative">
            <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl">
              <img 
                src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/3f933ef5-2341-4c5f-900c-41539c75ec1f.jpg" 
                alt="Vores vognpark og containere" 
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -top-6 -right-6 md:-top-10 md:-right-10 w-28 h-28 md:w-40 md:h-40 bg-amber-500 rounded-full flex items-center justify-center text-white shadow-2xl z-20 border-4 md:border-8 border-white">
              <div className="text-center">
                <p className="text-2xl md:text-4xl font-black leading-none">100%</p>
                <p className="text-[8px] md:text-[10px] uppercase font-bold tracking-widest">Pålidelig</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-sm uppercase tracking-[0.3em] font-black text-blue-900 mb-4">Hvorfor os?</h2>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900 mb-8 leading-tight">
              Vi leverer mere end <span className="text-blue-900">bare gods</span>
            </h3>
            <p className="text-slate-600 mb-12 text-lg leading-relaxed">
              Vores fundament er bygget på tillid, præcision og en dyb forståelse for vores kunders behov i lokalområdet.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              {whyChooseUs.map((item, index) => {
                const Icon = iconMap[item.icon];
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex flex-col space-y-4"
                  >
                    <div className="w-12 h-12 bg-white shadow-lg rounded-xl flex items-center justify-center text-blue-900">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-black text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Banner - Ultra Modern Bento Design */}
      <section className="py-32 relative overflow-hidden bg-slate-50">
        {/* Advanced Background Layers */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-200 blur-[120px] animate-pulse"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-amber-100 blur-[120px] opacity-50"></div>
        </div>
        
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/27ca744e-1835-42a7-88a9-98bfec53e1ab.jpg" 
            alt="Vores vognmandskørsel" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-white/80" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Main Message Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 bg-white/40 backdrop-blur-3xl border border-slate-200 rounded-[3rem] p-10 md:p-16 flex flex-col justify-center relative overflow-hidden group shadow-xl shadow-slate-200/50"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-blue-500/10 transition-colors duration-700"></div>
              
              <div className="relative z-10">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center space-x-3 bg-blue-50 border border-blue-100 px-5 py-2 rounded-full text-blue-900 text-xs font-black uppercase tracking-[0.2em] mb-8"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                  </span>
                  <span>Klar til afgang</span>
                </motion.div>
                
                <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-8 leading-[0.9]">
                  Skal vi hjælpe dig <br />
                  <span className="text-[#1d398e]">videre?</span>
                </h2>
                
                <p className="text-lg md:text-xl text-slate-600 mb-12 max-w-lg leading-relaxed font-medium">
                  Vi kombinerer 20 års erfaring med personlig service og pålidelighed. Lad os finde den optimale løsning for dig.
                </p>
                
                <div className="flex flex-wrap gap-6">
                  <Button to="/kontakt" variant="primary" className="rounded-full px-10 py-6 text-lg bg-blue-900 hover:bg-blue-800 shadow-[0_20px_50px_rgba(30,58,138,0.2)] border-none group">
                    Få et uforpligtende tilbud
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Bento Grid Contact Info */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* Phone Card */}
              <motion.a 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="sm:col-span-2 bg-gradient-to-br from-blue-900 to-blue-950 rounded-[2.5rem] p-8 flex flex-col justify-between group relative overflow-hidden shadow-2xl shadow-blue-900/10"
              >
                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform duration-500">
                  <Phone className="w-32 h-32 text-white" />
                </div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-white mb-12">
                    <Phone className="w-6 h-6" />
                  </div>
                  <p className="text-blue-100/70 text-sm font-bold uppercase tracking-widest mb-2">Ring direkte</p>
                  <p className="text-3xl md:text-4xl font-black text-white tracking-tight">{companyInfo.phone}</p>
                </div>
              </motion.a>

              {/* Email Card */}
              <motion.a 
                href={`mailto:${companyInfo.email}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/60 backdrop-blur-xl border border-slate-200 rounded-[2.5rem] p-8 flex flex-col justify-between group shadow-lg shadow-slate-200/50"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 mb-8 group-hover:bg-amber-600 group-hover:text-white transition-all">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Send mail</p>
                  <p className="text-lg font-black text-slate-900 break-all leading-tight">{companyInfo.email}</p>
                </div>
              </motion.a>

              {/* Support Card */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white/60 backdrop-blur-xl border border-slate-200 rounded-[2.5rem] p-8 flex flex-col justify-between group shadow-lg shadow-slate-200/50"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mb-8">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Vagttelefon</p>
                  <p className="text-lg font-black text-slate-900 leading-tight">Døgnvagt <br />24/7/365</p>
                </div>
              </motion.div>

              {/* Location Card */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="sm:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200 rounded-[2.5rem] p-8 flex items-center justify-between group shadow-lg shadow-slate-200/50"
              >
                <div className="flex items-center space-x-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-600">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Hovedkontor</p>
                    <p className="text-lg font-black text-slate-900">{companyInfo.address}</p>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 group-hover:bg-blue-900 group-hover:text-white transition-all">
                  <ArrowRight className="w-5 h-5" />
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </section>

    </>
  );
}
